import React, { forwardRef } from "react";
import styled, { useTheme } from "styled-components";
import classNames from "classnames";
import MountainMenuIcon from "../../icons/mountain_menu_icon.png";
import IBuildrProps from '../../utilities/IBuildrProps';
import ArrowRight from "../../images/arrow-right.svg";

export interface ISubNavContainerProps {
    mobileBgColor?: string;
    rolloverColor?: string;
}

export interface INavLinkProps extends IBuildrProps {
    name: string;
    url: string;
    selected?: boolean;
    onClick?: any;
    temporaryIsHashLink?: boolean;
}

export interface IHashLinkProps extends IBuildrProps {
    name: string;
    url: string;
    onClick?: any;
}

const SubNavContainer = styled.div<ISubNavContainerProps>`
    ul {
        .category {
            height: 80px;
            padding: 30px 20px;
            list-style: none;
            white-space: nowrap;

            a:hover {
                ${(props: ISubNavContainerProps) => `color: ${props.rolloverColor}`};
            }

            &.selected {
                background-image: url(${MountainMenuIcon});
                background-position: bottom;
                background-repeat: no-repeat;
            }
        }

        @media(max-width:767px) {
            overflow-x: auto;
            ${(props: ISubNavContainerProps) => `background-color: ${props.mobileBgColor}`};

            .category:last-child {
                padding-right: 60px;
                background-position-x: calc(50% - 15px);
            }
        }
    }
`;

// TODO: Remove 'temporaryIsHashLink' logic/prop and 'hash-link' classname after all HashNav 'NavLink' usages are replaced with the newer 'HashLink' widget
export const NavLink = styled(forwardRef(({ dataTestId, name, url, selected = false, temporaryIsHashLink, onClick = null, editorClassObj, cmsId, className }: INavLinkProps, ref: any) => {
    function handleClick(e) {
        if (typeof onClick === "function") {
            onClick(e, url);
        }
    }

    return (
        <li data-test-id={dataTestId} data-id={cmsId} ref={ref} className={classNames(className, { selected }, "nav-item category text-center text-primary", temporaryIsHashLink ? "hash-link d-flex" : "w-100",  editorClassObj)} key={name}>
            <a className={classNames({'px-lg-3 d-flex flex-grow-1 justify-center align-items-center text-white text-uppercase': temporaryIsHashLink}, "text-decoration-none")} href={url} onClick={cmsId ? e => {e.preventDefault()} : handleClick}>
                <strong className="text-center">
                    {name} <img src={ArrowRight} className="arrow d-lg-none pl-1" />
                </strong>
            </a>
        </li>
    );
}))`
&.hash-link {
    position: relative;
    min-height: 100%;
    strong {
        font-family: 'National2';
        font-size: .78rem;
        font-weight: 500;
        letter-spacing: 0.05rem;
        padding: .75rem;
    }
    @media (min-width:992px) {
        &:hover {
            &:after {
                content: '';
                display: block;
                height: 3px;
                width: 100%;
                background: var(--shadedRedRock);
                position: absolute;
                bottom: -1.5px;
                z-index: 100;
            }
        }
    }
}
`;

export const HashLink = styled(forwardRef(({ dataTestId, name, url, onClick, editorClassObj, cmsId, className }: IHashLinkProps, ref: any) => {
    return (
        <li data-test-id={dataTestId} data-id={cmsId} ref={ref} className={classNames(className, editorClassObj, 'd-flex')} key={name}>
            <a className="px-lg-3 d-flex flex-grow-1 align-items-center justify-content-center text-decoration-none text-white text-uppercase" href={url} onClick={cmsId ? e => {e.preventDefault()} : onClick}>
                <span className="text-center">
                    {name} <img src={ArrowRight} className="arrow d-lg-none pl-1"/>
                </span>
            </a>
        </li>
    );
}))`
position: relative;
min-height: 100%;
a {
    font-family: 'National2';
    font-size: .78rem;
    font-weight: 500;
    letter-spacing: 0.05rem;
}
span {
    padding: .75rem;
}
@media (min-width:992px) {
    &:hover {
        &:after {
            content: '';
            display: block;
            height: 3px;
            width: 100%;
            background: var(--shadedRedRock);
            position: absolute;
            bottom: -1.5px;
            z-index: 100;
        }
    }
}
`;

export interface ISubMenuProps extends ISubNavContainerProps {
    url?: string;
    bgColorClass?: string;
    children;
}

const SubNav = (props: ISubMenuProps) => {
    const theme = useTheme();
    const { bgColorClass = "bg-gray-100", mobileBgColor = "#f5f5f5", rolloverColor, children } = props;

    return (
        <SubNavContainer rolloverColor={rolloverColor || theme.primary} mobileBgColor={mobileBgColor} >
            <ul className={classNames(bgColorClass, "bg-gray-100 w-100 px-5 navbar-nav m-auto flex-row")}>
                {children}
            </ul>
        </SubNavContainer>
    );
};

export default SubNav;